/* eslint-disable no-restricted-syntax */
import React from 'react';
import cls from 'classnames';

import { useApiObject } from 'src/explore/hooks/useApiObject';
import { useShopConfig } from 'src/explore/hooks/useShopConfig';
import { IMegaphone } from 'src/explore/types/shoppe';
import { Markdown } from 'src/explore/elements/Markdown';
import { isNativeMobileApp } from 'src/explore/services/mobile';
import Countdown from 'react-countdown';

import moment from 'moment-timezone';
import styles from './index.module.sass';

const hex = ( color: string | null ) => {
  if ( !color ) return undefined;

  return color.replace( /(^[^#])/, '#$1' );
};

const parseCountdown = ( description: string, expiresAt: string ) => {
  if ( !description.match( /\{COUNTDOWN.*}/ )) return null;

  const countDown = description.match( /\{COUNTDOWN_[0-2][0-9]}/ ); // Do we still want the old approach?
  if ( countDown !== null ) {
    const countDownTime = countDown[0].match( /[0-2][0-9]/ );
    const countDownDate = moment()
      .tz( 'America/New_York' )
      .startOf( 'day' )
      .hours( parseInt( countDownTime[0], 10 ));
    if ( moment().diff( countDownDate ) > 0 ) {
      countDownDate.add( 1, 'day' );
    }
    return countDownDate;
  }
  if ( !expiresAt ) return null;
  return moment( expiresAt );
};

export const Megaphone = () => {
  const config = useShopConfig();

  const [ activeMessage, setActiveMessage ] = React.useState<IMegaphone | null>( null );

  const { data } = useApiObject<{ data: IMegaphone[] }>( '/megaphone_messages', {
    apiPathOverride: '/api/v4/',
    camelCaseKeys: true,
  });

  // Find the first megaphone for this page
  // megaphones are already sorted by priority on the backend
  // app/controllers/explore/api/megaphone_messages_controller.rb
  React.useEffect(() => {
    let current: IMegaphone = null;

    if ( data?.data ) {
      for ( const message of data.data ) {
        for ( const regex of message.regexes ) {
          if ( window.location.pathname.match( regex )) {
            current = message;
            break;
          }
        }
        if ( current ) {
          break;
        }
      }
    }

    if ( current ) {
      setActiveMessage({
        ...current,
        backgroundColor: hex( current.backgroundColor ),
        textColor: hex( current.textColor ),
      });
    }
  }, [ data ]);

  if ( !activeMessage || isNativeMobileApp()) return null;

  const { backgroundColor, description, expiresAt, icon, title, megaphoneType, textColor } =
    activeMessage;
  const descriptionWithoutCountdown = description.replace( /\{COUNTDOWN.*}/, '' );
  const countDownDate = parseCountdown( description, expiresAt );

  return (
    <div className="bs4">
      <div
        className={cls( styles.wrapper, styles[megaphoneType], {
          [styles.whitelabel]: config.isWhitelabel,
          [styles[config.merchantPermalink]]: !!config.merchantPermalink,
          [styles.withCountdown]: countDownDate !== null,
        })}
        style={{ backgroundColor, color: textColor }}
      >
        <div
          className={cls(
            styles.container,
            'd-flex flex-column align-items-center justify-content-center mx-auto'
          )}
        >
          {icon && <div className="fs-9 mx-2">{icon}</div>}
          {title && (
            <div className={cls( styles.title, 'font-weight-bold' )}>
              {title.trim()}
              <span className={styles.titleSpace}>&nbsp;</span>
            </div>
          )}
          <div className={cls( styles.descriptionContainer )}>
            {descriptionWithoutCountdown && (
              <Markdown
                className={cls(
                  styles.markdown,
                  { 'fs-3': megaphoneType === 'marketing' },
                  styles.description
                )}
              >
                {descriptionWithoutCountdown}
              </Markdown>
            )}
            {countDownDate !== null && (
              <Countdown className={styles.countdown} daysInHours date={countDownDate.format( '' )} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
